/**
 * A single-slot memo keyed on a string fingerprint.
 *
 * Built for values that are recomputed from outside a React render — TanStack
 * `form.Subscribe` selectors and form-level validators are plain functions, so
 * `useMemo` is not available to them, and `useSyncExternalStoreWithSelector`
 * re-invokes a selector on every store emission *and* every render.
 *
 * Returning the previous reference on a key hit is the second half of the point.
 * `form.Subscribe` compares the selector's result with `===` on the WHOLE value
 * (`@tanstack/react-store`'s `useSelector` default), so a freshly built array
 * re-renders the subtree even when its contents are identical. To collect that
 * half, a selector has to return the memoized value *directly* — wrap it in a
 * fresh object literal and only the compute saving survives, which is the deal
 * most of the scenario-form selectors currently take.
 *
 * Close cousin: `makeArrayStabilizer` in `pages/packages/new/atoms.ts`. That one
 * stabilizes a value already computed; this one owns the computation and can
 * skip it, which is what INS-12009 needed. INS-12071 tracks converging the two.
 */

/**
 * Consecutive alternating misses before the dev-only thrash warning fires.
 *
 * Not 1: a single A→B→A is ordinary form editing (add an area, then remove it,
 * and the key returns to a value it held before), which is a legitimate miss
 * and must stay quiet. Two callers interleaving against one slot, on the other
 * hand, alternate on *every* call and blow past this immediately.
 */
const THRASH_ALTERNATIONS_BEFORE_WARNING = 4;

export interface KeyedMemo<T> {
  /**
   * Returns the cached value when `key` is unchanged, otherwise recomputes.
   * @param {string} key - Fingerprint of everything `compute` depends on
   * @param {() => T} compute - Produces the value on a miss
   * @return {T} The cached or freshly computed value
   */
  get(key: string, compute: () => T): T;
  /** Drops the cached value. Primarily for test isolation. */
  reset(): void;
}

/**
 * Creates a single-slot keyed memo.
 *
 * One slot is deliberate: these caches back a single mounted form at a time, so
 * a larger cache would buy nothing. If two consumers ever alternate keys against
 * the same memo it degrades to recomputing every call — slower, never wrong,
 * because the key carries every input the computation reads.
 *
 * "Never wrong" only covers the *value*, though. For a memo whose point is the
 * stable *reference* (`getMemoizedMapAreas`), thrash hands back a fresh array
 * every call and quietly reinstates the bug the memo was added to fix, with
 * nothing failing. Hence the development-only warning: it is the only signal
 * that would ever surface that failure mode.
 * @param {string} [label] - Names this memo in the dev thrash warning
 * @return {KeyedMemo<T>} The memo
 */
export function createKeyedMemo<T>(label = 'a keyed memo'): KeyedMemo<T> {
  let cachedKey: string | null = null;
  let cached: T | undefined;

  // Dev-only thrash detection. `lastMissKey` / `priorMissKey` are the keys of
  // the previous two misses, so "this miss wants the key from two misses ago"
  // is one comparison — that is what alternation looks like from inside a
  // one-slot cache. All of it lives behind the NODE_ENV check below, so
  // production pays nothing: Vite statically replaces NODE_ENV and the block is
  // dropped from the bundle.
  let lastMissKey: string | null = null;
  let priorMissKey: string | null = null;
  let alternations = 0;
  let warned = false;

  const noteMiss = (key: string): void => {
    alternations = key === priorMissKey ? alternations + 1 : 0;
    priorMissKey = lastMissKey;
    lastMissKey = key;

    // Once only. A thrashing caller alternates forever, and a warning per call
    // would bury the console — and with it any other diagnostic in the page.
    if (warned || alternations < THRASH_ALTERNATIONS_BEFORE_WARNING) return;
    warned = true;
    console.warn(
      `createKeyedMemo: ${label} is being called with two keys in alternation, ` +
        'so every call is a miss. The memo has one slot by design — give each ' +
        'caller its own instance. If callers rely on the returned reference ' +
        'being stable, they are silently not getting it.'
    );
  };

  return {
    get(key: string, compute: () => T): T {
      if (cachedKey !== key) {
        // Compute first: a throw has to leave the slot on its previous key,
        // otherwise the next call hits the new key against a stale value.
        cached = compute();
        cachedKey = key;
        // After the compute, for the same reason — a miss that threw is not a
        // miss this slot ever served, so it should not count toward thrash.
        if (process.env.NODE_ENV === 'development') noteMiss(key);
      }
      return cached as T;
    },
    reset(): void {
      cachedKey = null;
      cached = undefined;
      lastMissKey = null;
      priorMissKey = null;
      alternations = 0;
      warned = false;
    },
  };
}
